import React from 'react';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Button,
  Text
} from '@chakra-ui/react';
import { RepeatIcon, WarningIcon } from '@chakra-ui/icons';
import logger from '../../utils/logger';

/**
 * Error boundary specifically for file upload operations
 * Catches JavaScript errors and provides user-friendly feedback
 */
class FileUploadErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };

    this.logger = logger.create('FileUploadErrorBoundary');
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.logger.error('File upload error caught by boundary', {
      error: error.toString(),
      errorInfo,
      stack: error.stack
    });

    this.setState({
      error,
      errorInfo
    });

    if (window.reportError) {
      window.reportError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));

    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  render() {
    const { hasError, error, errorInfo, retryCount } = this.state;
    const { userFriendlyMessage, onCancel, children } = this.props;
    const bgDebug = 'gray.100';

    if (hasError) {
      return (
        <Box p={6} textAlign="center">
          <Alert
            status="error"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            borderRadius="md"
            mb={4}
          >
            <AlertIcon as={WarningIcon} boxSize="24px" />
            <AlertTitle mt={2} fontSize="lg">
              حدث خطأ أثناء رفع الملفات
            </AlertTitle>
            <AlertDescription fontSize="sm" color="gray.600" mt={2}>
              {userFriendlyMessage || 'حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.'}
            </AlertDescription>
          </Alert>

          <Box display="flex" gap={4} justifyContent="center" flexWrap="wrap" mt={4}>
            <Button
              colorScheme="red"
              leftIcon={<RepeatIcon />}
              onClick={this.handleRetry}
              isDisabled={retryCount >= 3}
            >
              {retryCount >= 3 ? 'تم تجاوز عدد المحاولات' : 'إعادة المحاولة'}
            </Button>

            {onCancel && (
              <Button variant="outline" onClick={onCancel}>
                إلغاء
              </Button>
            )}
          </Box>

          {process.env.NODE_ENV === 'development' && error && (
            <Box
              mt={6}
              p={4}
              bg={bgDebug}
              rounded="md"
              textAlign="left"
              fontSize="xs"
              overflow="auto"
              maxHeight="200px"
              whiteSpace="pre-wrap"
            >
              <Text fontWeight="bold" mb={2}>تفاصيل الخطأ (للمطورين):</Text>
              <Text>{error.toString()}</Text>
              <Text>{errorInfo?.componentStack}</Text>
            </Box>
          )}
        </Box>
      );
    }

    return children;
  }
}

/**
 * Hook for handling upload errors in functional components
 */
export const useFileUploadErrorHandler = (onError) => {
  return React.useCallback((error, context = {}) => {
    const uploadLogger = logger.create('FileUploadError');

    uploadLogger.error('File upload error in functional component', {
      error: error.toString(),
      context,
      stack: error.stack
    });

    let userMessage = 'حدث خطأ أثناء رفع الملف';

    if (error.name === 'NetworkError' || error.code === 'NETWORK_ERROR') {
      userMessage = 'خطأ في الاتصال بالشبكة. يرجى التحقق من اتصال الإنترنت';
    } else if (error.response?.status === 413) {
      userMessage = 'حجم الملف كبير جداً';
    } else if (error.response?.status === 415) {
      userMessage = 'نوع الملف غير مدعوم';
    } else if (error.response?.status >= 500) {
      userMessage = 'خطأ في الخادم. يرجى المحاولة لاحقاً';
    } else if (error.message) {
      userMessage = error.message;
    }

    if (onError) {
      onError({
        originalError: error,
        userMessage,
        context
      });
    }
  }, [onError]);
};

/**
 * HOC wrapper
 */
export const withFileUploadErrorBoundary = (WrappedComponent, errorBoundaryProps = {}) => {
  return function FileUploadErrorBoundaryWrapper(props) {
    return (
      <FileUploadErrorBoundary {...errorBoundaryProps}>
        <WrappedComponent {...props} />
      </FileUploadErrorBoundary>
    );
  };
};

export default FileUploadErrorBoundary;